import React from 'react';
import Icon from '../../../components/AppIcon';

const PhilosophySection = () => {
  const philosophyPillars = [
    {
      icon: "Zap",
      title: "AI as Survival Strategy",
      description: "AI transformation isn\'t an optional efficiency upgrade—it\'s an existential business imperative. Organizations that fail to build internal AI capabilities will become extinct in the AI-driven economy.",
      stats: "73% of executives believe AI is critical for survival",
      trend: "up"
    },
    {
      icon: "Unlock",
      title: "Open-Source Superiority",
      description: "Proprietary AI solutions create dependency, limit innovation, and transfer control to vendors. Open-source AI provides transparency, customization, and true ownership of your AI destiny.",
      stats: "85% cost reduction with open-source AI",
      trend: "up"
    },
    {
      icon: "Shield",
      title: "Vendor Independence",
      description: "Every dollar spent on proprietary AI is a vote for your own dependency. True competitive advantage comes from capabilities you own, not services you rent.",
      stats: "60% of AI projects fail due to vendor lock-in",
      trend: "down"
    },
    {
      icon: "Building",
      title: "Internal Capability Building",
      description: "Sustainable AI transformation requires building internal expertise and capabilities. Outsourcing AI thinking is outsourcing your competitive advantage.",
      stats: "3x faster innovation with internal AI teams",
      trend: "up"
    }
  ];

  const researchData = [
    {
      statistic: "89%",
      description: "of enterprises report vendor lock-in concerns with proprietary AI",
      source: "Gartner AI Survey 2024"
    },
    {
      statistic: "$2.1T",
      description: "projected AI market size by 2030, dominated by open-source solutions",
      source: "McKinsey Global Institute"
    },
    {
      statistic: "67%",
      description: "of AI leaders prefer open-source for strategic AI initiatives",
      source: "MIT Technology Review"
    },
    {
      statistic: "4.2x",
      description: "faster time-to-market with internal AI capabilities",
      source: "Harvard Business Review"
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-brand-primary/10 rounded-full px-4 py-2 mb-6">
            <Icon name="Brain" size={16} className="text-brand-primary" />
            <span className="text-sm font-medium text-brand-primary">Our Philosophy</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-6">
            AI Independence is
            <span className="block text-brand-primary">Organizational Sovereignty</span>
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Our core beliefs are backed by industry research and validated by hundreds of successful transformations. This isn't theory—it's proven strategy.
          </p>
        </div>

        {/* Philosophy Pillars */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {philosophyPillars?.map((pillar, index) => (
            <div key={index} className="bg-brand-surface rounded-xl p-8 border border-gray-200 hover:shadow-executive transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={pillar?.icon} size={24} className="text-brand-primary" />
                  </div>
                </div>
                
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-text-primary mb-3">
                    {pillar?.title}
                  </h3>
                  
                  <p className="text-text-secondary mb-4 leading-relaxed">
                    {pillar?.description}
                  </p>
                  
                  <div className="flex items-center space-x-2">
                    <div className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${
                      pillar?.trend === 'up' ?'bg-green-100 text-green-800' :'bg-red-100 text-red-800'
                    }`}>
                      <Icon 
                        name={pillar?.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} 
                        size={14} 
                      />
                      <span>{pillar?.stats}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Research Data */}
        <div className="bg-gradient-to-br from-brand-primary to-blue-800 rounded-2xl p-8 lg:p-12 text-white">
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold mb-4">
              Industry Research Validates Our Approach
            </h3>
            <p className="text-blue-100 text-lg">
              Leading research organizations confirm what we've known all along
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {researchData?.map((data, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-brand-accent mb-2">
                  {data?.statistic}
                </div>
                <p className="text-blue-100 mb-3 leading-relaxed">
                  {data?.description}
                </p>
                <p className="text-xs text-blue-200 opacity-80">
                  {data?.source}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhilosophySection;